import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useBlockerContext } from "containers/App/RouteCompat";

const DEFAULT_MESSAGE = "You have unsaved changes. Are you sure you want to leave this page?";

/**
 * Prompts the user before navigating away (via sidebar links, programmatic
 * navigation, browser back/forward, refresh, or tab close) while there are
 * unsaved changes.
 *
 * Relies on the BlockerProvider in containers/App/RouteCompat, which wraps
 * React Router's NavigationContext so that all <Link> clicks and useNavigate()
 * calls are intercepted. The provider also owns the global `beforeunload`
 * listener and a `popstate` listener that handles browser back/forward via a
 * sentinel-state pattern - this hook just needs to register/unregister a
 * block callback.
 *
 * For browser back/forward, the BlockerProvider invokes the callback with
 * `{ pathname: "" }` (no destination is known up-front for a pop), so the
 * `current !== target` check below always reports the navigation as
 * "different page" and yields the confirmation message - which is what we
 * want, since pressing back IS leaving the current page.
 *
 * @param hasUnsavedChanges - When true, navigation away from the current
 *                            pathname will prompt the user for confirmation.
 * @param message - Optional custom confirmation message.
 */
const useUnsavedChangesBlocker = (hasUnsavedChanges: boolean, message: string = DEFAULT_MESSAGE) => {
  const blockerContext = useBlockerContext();
  const location = useLocation();

  useEffect(() => {
    if (!hasUnsavedChanges || !blockerContext?.block) return;

    const unblock = blockerContext.block((newLocation: { pathname: string }) => {
      if (location.pathname !== newLocation.pathname) {
        return message;
      }
      return undefined;
    });

    return () => {
      unblock?.();
    };
  }, [hasUnsavedChanges, location.pathname, message, blockerContext]);
}

export default useUnsavedChangesBlocker;
